import { Modal, Steps } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { SourceBuilderState, StepProps } from './types';
import { CreateScheduleStep } from './CreateScheduleStep';
import { DefineRecipeStep } from './DefineRecipeStep';
import { NameSourceStep } from './NameSourceStep';
import { SelectTemplateStep } from './SelectTemplateStep';
import { useTranslation } from '../../../../i18n/useTranslation';
import {
    TestConnectionPollingProvider,
    useTestConnectionPolling,
} from './RecipeForm/TestConnection/TestConnectionPollingContextAPIs';
import sourcesJson from './sources.json';

/**
 * Mapping from the step type to the title for the step
 */
export enum IngestionSourceBuilderStepTitles {
    SELECT_TEMPLATE = 'Choose Type',
    DEFINE_RECIPE = 'Configure Recipe',
    CREATE_SCHEDULE = 'Schedule Ingestion',
    NAME_SOURCE = 'Finish up',
}

/**
 * Mapping from the step type to the component implementing that step.
 */
export const IngestionSourceBuilderStepComponent = {
    SELECT_TEMPLATE: SelectTemplateStep,
    DEFINE_RECIPE: DefineRecipeStep,
    CREATE_SCHEDULE: CreateScheduleStep,
    NAME_SOURCE: NameSourceStep,
};

/**
 * Steps of the Ingestion Source Builder flow.
 */
export enum IngestionSourceBuilderStep {
    SELECT_TEMPLATE = 'SELECT_TEMPLATE',
    DEFINE_RECIPE = 'DEFINE_RECIPE',
    // CREATE_SCHEDULE = 'CREATE_SCHEDULE',
    NAME_SOURCE = 'NAME_SOURCE',
}

type Props = {
    initialState?: SourceBuilderState;
    visible: boolean;
    onSubmit?: (input: SourceBuilderState, resetState: () => void, shouldRun?: boolean) => void;
    onCancel?: (resetState: () => void) => void;
};

// Inner component that uses the TestConnectionPolling context
const IngestionSourceBuilderModalContent = ({ initialState, visible, onSubmit, onCancel }: Props) => {
    const { t } = useTranslation();
    const isEditing = initialState !== undefined;
    const titleText = isEditing ? t('Edit Ingestion Source') : t('New Ingestion Source');
    const initialStep = isEditing
        ? IngestionSourceBuilderStep.DEFINE_RECIPE
        : IngestionSourceBuilderStep.SELECT_TEMPLATE;

    const { resetTestConnection } = useTestConnectionPolling();

    const [stepStack, setStepStack] = useState([initialStep]);
    const [ingestionBuilderState, setIngestionBuilderState] = useState<SourceBuilderState>({});

    const ingestionSources = JSON.parse(JSON.stringify(sourcesJson)); // TODO: replace with call to server once we have access to dynamic list of sources

    // Reset the ingestion builder modal state when the modal is re-opened.
    const prevInitialState = useRef(initialState);
    useEffect(() => {
        if (!isEqual(prevInitialState.current, initialState)) {
            setIngestionBuilderState(initialState || {});
        }
        prevInitialState.current = initialState;
    }, [initialState]);

    // Reset the step stack to the initial step when the modal is re-opened.
    useEffect(() => setStepStack([initialStep]), [initialStep]);

    const goTo = (step: IngestionSourceBuilderStep) => {
        setStepStack([...stepStack, step]);
    };

    const prev = () => {
        if (stepStack[stepStack.length - 1] === IngestionSourceBuilderStep.DEFINE_RECIPE) {
            resetTestConnection();
        }
        setStepStack(stepStack.slice(0, -1));
    };

    const cancel = () => {
        // Reset test connection state immediately
        resetTestConnection();
        // Use setTimeout to ensure state updates are processed before callback
        onCancel?.(() => {
            setStepStack([initialStep]);
            setIngestionBuilderState({});
        });
    };

    const submit = (shouldRun?: boolean) => {
        resetTestConnection();
        onSubmit?.(
            ingestionBuilderState,
            () => {
                setStepStack([initialStep]);
                setIngestionBuilderState({});
            },
            shouldRun,
        );
    };

    const currentStep = stepStack[stepStack.length - 1];
    const currentStepIndex = Object.values(IngestionSourceBuilderStep)
        .map((value, index) => ({
            value,
            index,
        }))
        .filter((obj) => obj.value === currentStep)[0].index;
    const StepComponent: React.FC<StepProps> = IngestionSourceBuilderStepComponent[currentStep];

    return (
        <Modal
            footer={null}
            visible={visible}
            className="f-modal"
            onCancel={cancel}
            title={<h1>{titleText}</h1>}
            wrapClassName="f-modal-vertical-scroll f-ingestion-source-builder-modal"
        >
            <Steps labelPlacement="vertical" className="f-custom-steps mb-4" current={currentStepIndex}>
                {Object.keys(IngestionSourceBuilderStep).map((item) => (
                    <Steps.Step key={item} title={t(IngestionSourceBuilderStepTitles[item])} />
                ))}
            </Steps>
            <StepComponent
                goTo={goTo}
                submit={submit}
                cancel={cancel}
                state={ingestionBuilderState}
                ingestionSources={ingestionSources}
                updateState={setIngestionBuilderState}
                prev={stepStack.length > 1 ? prev : undefined}
            />
        </Modal>
    );
};

export const IngestionSourceBuilderModal = ({ initialState, visible, onSubmit, onCancel }: Props) => {
    return (
        <TestConnectionPollingProvider>
            <IngestionSourceBuilderModalContent
                initialState={initialState}
                visible={visible}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        </TestConnectionPollingProvider>
    );
};
